import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import { Goal, GoalStatus, GoalPriority } from '../types/goal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Loading from './Loading';
import Sidebar from './Sidebar';

export function Goals() {
  const { profile } = useUser();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<string | null>(null);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'active' as GoalStatus,
    priority: 'medium' as GoalPriority,
    start_date: '',
    target_date: '',
  });

  const fetchGoals = useCallback(async () => {
    if (!profile) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching goals:', error);
    } else {
      setGoals(data || []);
    }
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const handleOpenDialog = (goal?: Goal) => {
    if (goal) {
      setEditingGoal(goal);
      setFormData({
        title: goal.title,
        description: goal.description || '',
        status: goal.status,
        priority: goal.priority,
        start_date: goal.start_date || '',
        target_date: goal.target_date || '',
      });
    } else {
      setEditingGoal(null);
      setFormData({
        title: '',
        description: '',
        status: 'active',
        priority: 'medium',
        start_date: '',
        target_date: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGoal(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    const goalData = {
      ...formData,
      user_id: profile.id,
      updated_at: new Date().toISOString(),
    };

    if (editingGoal) {
      const { error } = await supabase
        .from('goals')
        .update(goalData)
        .eq('id', editingGoal.id);

      if (error) {
        console.error('Error updating goal:', error);
        return;
      }
    } else {
      const { error } = await supabase
        .from('goals')
        .insert([{ ...goalData, created_at: new Date().toISOString() }]);

      if (error) {
        console.error('Error creating goal:', error);
        return;
      }
    }

    handleCloseDialog();
    fetchGoals();
  };

  const handleDeleteClick = (goalId: string) => {
    setGoalToDelete(goalId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!goalToDelete) return;

    const { error } = await supabase
      .from('goals')
      .delete()
      .eq('id', goalToDelete);

    if (error) {
      console.error('Error deleting goal:', error);
      return;
    }

    setOpenDeleteDialog(false);
    setGoalToDelete(null);
    fetchGoals();
  };

  const handleComplete = async (goal: Goal) => {
    const { error } = await supabase
      .from('goals')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', goal.id);

    if (error) {
      console.error('Error completing goal:', error);
      return;
    }

    fetchGoals();
  };

  if (loading) return <Loading />;
  if (!profile) return null;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar user={profile} />
      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Goals
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenDialog()}
          >
            New Goal
          </Button>
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          {goals.map((goal) => (
            <Card key={goal.id}>
              <CardHeader
                title={goal.title}
                subheader={`Priority: ${goal.priority} | Status: ${goal.status}`}
                action={
                  <Box>
                    <IconButton onClick={() => handleOpenDialog(goal)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteClick(goal.id)}>
                      <DeleteIcon />
                    </IconButton>
                    {goal.status !== 'completed' && (
                      <IconButton onClick={() => handleComplete(goal)}>
                        <CheckCircleIcon />
                      </IconButton>
                    )}
                  </Box>
                }
              />
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {goal.description}
                </Typography>
                {goal.target_date && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Target Date: {new Date(goal.target_date).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle>
              {editingGoal ? 'Edit Goal' : 'New Goal'}
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as GoalStatus })}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as GoalPriority })}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Start Date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Target Date"
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary">
                {editingGoal ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>Delete Goal</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this goal?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
} 